import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { listSubscriptions } from '@/lib/api/admin';
import type { AdminSubscriptionRow, SubscriptionStanding } from '@/lib/api/types';
import { formatDate } from '@/lib/format';
import { useUrlParam } from '@/lib/hooks';
import { Badge, type Tone } from '@/components/ui/badge';
import { DataTable, type Column } from '@/components/ui/data-table';
import { PageHeader, Segmented, Toolbar } from '@/components/ui/page';

const STANDING: Record<SubscriptionStanding, { label: string; tone: Tone }> = {
  ok: { label: 'В силе', tone: 'neutral' },
  expiring: { label: 'Истекает', tone: 'warn' },
  grace: { label: 'Льгота', tone: 'warn' },
  expired: { label: 'Истёк', tone: 'danger' },
};

const STATUS_LABEL: Record<string, string> = { trialing: 'Пробный период', active: 'Активна', past_due: 'Просрочена', suspended: 'Приостановлена', cancelled: 'Отменена' };

type Filter = 'all' | SubscriptionStanding;

/** Who runs out: every organization's subscription, soonest first, with where it stands. */
export function SubscriptionsPage() {
  const navigate = useNavigate();
  const subs = useQuery({ queryKey: ['admin', 'subscriptions'], queryFn: listSubscriptions });
  const [filter, setFilter] = useUrlParam('standing', 'all');

  const rows = useMemo(
    () => (subs.data ?? []).filter((r) => filter === 'all' || r.standing === filter),
    [subs.data, filter],
  );

  const columns: Column<AdminSubscriptionRow>[] = [
    { key: 'org', header: 'Организация', sortValue: (r) => r.organizationName, render: (r) => <span className="font-semibold text-ink">{r.organizationName}</span> },
    {
      key: 'plan',
      header: 'Тариф',
      sortValue: (r) => r.planCode,
      render: (r) => (
        <span className="flex flex-wrap items-center gap-1.5">
          {r.planName}
          {r.effectivePlanCode !== r.planCode && <Badge tone="warn">действует {r.effectivePlanCode}</Badge>}
          {r.provider === 'manual' && <Badge tone="accent">вручную</Badge>}
        </span>
      ),
    },
    { key: 'status', header: 'Статус', sortValue: (r) => r.status, hideBelow: 'md', render: (r) => STATUS_LABEL[r.status] ?? r.status },
    { key: 'until', header: 'Доступ до', sortValue: (r) => r.entitledUntil, className: 'whitespace-nowrap', render: (r) => r.entitledUntil ? formatDate(r.entitledUntil) : '—' },
    { key: 'standing', header: 'Положение', align: 'center', sortValue: (r) => r.standing, render: (r) => <Badge tone={STANDING[r.standing].tone} dot>{STANDING[r.standing].label}</Badge> },
  ];

  return (
    <>
      <PageHeader title="Подписки" subtitle="Кто на каком тарифе и у кого кончается срок. Продлить — в карточке организации." />
      <Toolbar className="mb-3">
        <Segmented<Filter>
          ariaLabel="Положение"
          value={filter as Filter}
          onChange={setFilter}
          options={[
            { value: 'all', label: 'Все' },
            { value: 'expiring', label: 'Истекают' },
            { value: 'grace', label: 'Льгота' },
            { value: 'expired', label: 'Истекли' },
          ]}
        />
      </Toolbar>
      <DataTable
        id="s_"
        rows={rows}
        columns={columns}
        rowKey={(r) => r.organizationId}
        defaultSort={{ key: 'until', dir: 'asc' }}
        loading={subs.isPending}
        error={subs.error}
        onRetry={() => subs.refetch()}
        onRowClick={(r) => navigate(`/organizations/${r.organizationId}`)}
        empty={{ icon: <CalendarClock size={28} />, title: filter === 'all' ? 'Подписок нет' : 'Таких подписок нет' }}
      />
    </>
  );
}
